import { FC, ReactNode, useState } from 'react';
import { Dropdown } from '../../components/dropdown/dropdown';
import { pageKey } from '../../helpers/path';
import { useQueryParams } from '../../hooks/use-query-params';
import { IdValue } from '../../types/props';
import { DropdownFilterDateTimeRange } from './dropdown-filter-date-time-range/dropdown-filter-date-time-range';
import { DropdownFilterInput } from './dropdown-filter-input/dropdown-filter-input';
import { DropdownFilterMultiSelect } from './dropdown-filter-multi-select/dropdown-filter-multi-select';
import { DropdownFilterRange } from './dropdown-filter-range/dropdown-filter-range';
import { DropdownFilterSingleSelect } from './dropdown-filter-single-select/dropdown-filter-single-select';

type FilterType = 'datetime-range' | 'multi-select' | 'single-select' | 'input' | 'range';

export type FilterOptionType = {
  label: ReactNode;
  value: string;
  filter: FilterType;
  filterOptions?: IdValue[];
  suggestions?: string[];
};

interface Props {
  label: ReactNode;
  options: FilterOptionType[];
  leftIcon?: ReactNode;
  className?: string;
}

export const DropdownFilter: FC<Props> = (props) => {
  const { label, options, leftIcon, className } = props;
  const { getQueryParams, addQueryParams } = useQueryParams();
  const params = getQueryParams();
  const [selectedFilter, setSelectedFilter] = useState<string>();

  const renderFilter = (option: FilterOptionType) => {
    const commonProps = {
      name: option.value,
      isOpen: selectedFilter === option.value,
      onOpen: (open: boolean) => {
        if (!open) setSelectedFilter(undefined);
      },
    };

    switch (option.filter) {
      case 'datetime-range':
        return <DropdownFilterDateTimeRange {...commonProps} />;
      case 'multi-select':
        return <DropdownFilterMultiSelect {...commonProps} options={option.filterOptions} />;
      case 'single-select':
        return <DropdownFilterSingleSelect {...commonProps} options={option.filterOptions} />;
      case 'input':
        return <DropdownFilterInput {...commonProps} suggestOptions={option.suggestions} />;
      case 'range':
        return <DropdownFilterRange {...commonProps} />;
    }
  };

  const handleReset = () => {
    const resetParams = Object.fromEntries(options.map(({ value }) => [value, undefined]));
    addQueryParams({ ...resetParams, [pageKey]: '1' });
    setSelectedFilter(undefined);
  };

  const dropdownOptions = options.map((opt) => ({
    key: opt.value,
    label: opt.label,
    content: renderFilter(opt),
    onClick: () => setSelectedFilter(opt.value),
    isActive: !!params[opt.value],
  }));

  return (
    <Dropdown
      label={label}
      leftIcon={leftIcon}
      className={className}
      options={dropdownOptions}
      withReset
      onReset={handleReset}
    />
  );
};
